import React from "react";

import { IViewProps, View } from "core/decorators/View";
import Logger from "core/util/Logger";
import MultimediaViewConfig from "./MultimediaViewConfig";

/**
 * renders jwplayer and provides react bindings for events
 *
 * @author chills
 */

interface IMultimediaViewProps extends IViewProps<MultimediaViewConfig> {
	src: string;
	poster?: string;
	audioMode?: boolean; // force the player to be 40px tall
	aspectratio?: string; // 16:9
	onReady?: () => void;
	onTimeUpdate?: (o: { duration: number; position: number; }) => void;
}

interface ITranscriptNode {
	start: number;
	text: string;
}

interface IMultimediaViewState {
	transcript: ITranscriptNode[];
	active: number;
	cycle: number;
	query: string;
	autoScroll: boolean;
}

class Player extends React.Component<IMultimediaViewProps, IMultimediaViewState> {
	private mediaRef: React.RefObject<HTMLDivElement>;
	private player: any = null;

	public constructor(props: IMultimediaViewProps) {
		super(props);
		this.mediaRef = React.createRef();
	}

	public render() {
		return <div ref={this.mediaRef} />;
	}

	public componentDidMount() {
		this.setupPlayer();
	}

	public componentDidUpdate(prevProps: IMultimediaViewProps) {
		// render the player if not currently rendered or the src changes
		if ((prevProps.src !== this.props.src) || !this.isRendered()) {
			this.setupPlayer();
		}
	}

	public componentWillUnmount() {
		// remove player on unmount?
		if (this.player) {
			this.player.remove();
			this.player = null;
		}
	}

	/**
	 * create the jwplayer instance
	 */
	private initPlayer = () => {
		const { current } = this.mediaRef;
		if (current && typeof window['jwplayer'] === 'function') {
			this.player = window['jwplayer'](current);
		}
	}

	private isRendered = (): boolean => {
		const { current } = this.mediaRef;
		return !!current && !!current.className && current.className.indexOf('jwplayer') > -1;
	}

	private setupPlayer = () => {
		if (!this.player) {
			this.initPlayer();
		}

		if (this.player) {

			const o = {
				file: this.props.src,
				image: this.props.poster,
				width: '100%'
			};

			if (this.props.audioMode) {
				o['height'] = '40px';
			} else {
				let aspectratio = "16:9";
				if (this.props.aspectratio && this.props.aspectratio.match(/[0-9]+\:[0-9]+/)) {
					aspectratio = this.props.aspectratio;
				}
				o['aspectratio'] = aspectratio;
			}

			this.player.setup(o);

			if (this.props.onReady) {
				this.player.on('ready', this.props.onReady);
			}

			if (this.props.onTimeUpdate) {
				this.player.on('time', this.props.onTimeUpdate);
			}
		} else {
			Logger.error("Failed to initialize media player");
		}
	}

	// control the player via ref
	public seek = (position: number): undefined => this.player && this.player.seek(position) || undefined;
	public getDuration = (): number => this.player && this.player.getDuration() || 0;
}

export type PlayerRef = React.RefObject<Player>;

@View({
	id: "multimedia",
	configClass: MultimediaViewConfig
})
export default class MultimediaView extends React.Component<IMultimediaViewProps & { playerRef: PlayerRef; }, IMultimediaViewState> {

	public render() {
		const { playerRef, ...rest } = this.props;
		return <Player {...rest} ref={playerRef} />;
	}
}



// WEBPACK FOOTER //
// ./src/common/views/multimedia/Multimedia.view.tsx